import { getAIModel } from './aiModelService';
import { getOpenAIApiKey, getProviderApiKey } from './apiKeyService';

/**
 * Central service for handling AI provider API calls
 * This service will route requests to the appropriate provider based on the model configuration
 */

/**
 * Make an API call to the appropriate AI provider based on the model configuration
 * @param {string} modelId - The ID of the AI model to use
 * @param {Object} requestData - The data to send to the AI provider
 * @returns {Promise<Object>} - The response from the AI provider
 */
export const generateContent = async (modelId, requestData) => {
  try {
    // Get the model configuration
    const model = await getAIModel(modelId);
    if (!model) {
      throw new Error(`Model with ID ${modelId} not found`);
    }

    // Check if the model is enabled
    if (model.isEnabled === false) {
      throw new Error(`Model ${model.displayName} is currently disabled`);
    }

    // Route to the appropriate provider
    switch (model.provider) {
      case 'openai':
        return await callOpenAI(model, requestData);
      case 'deepseek':
        return await callDeepSeek(model, requestData);
      case 'gemini':
        return await callGemini(model, requestData);
      default:
        throw new Error(`Unsupported provider: ${model.provider}`);
    }
  } catch (error) {
    console.error('Error generating content:', error);
    throw error;
  }
};

/**
 * Make an API call to OpenAI
 * @param {Object} model - The model configuration
 * @param {Object} requestData - The data to send to OpenAI
 * @returns {Promise<Object>} - The response from OpenAI
 */
const callOpenAI = async (model, requestData, userId) => {
  try {
    const { messages, temperature = 0.7, max_tokens = 2000, images = [], modelType } = requestData;
    
    // Check if this is a vision request with images
    // Use the explicit modelType parameter or fall back to model.type
    const isVisionRequest = (modelType === 'vision' || model.type === 'vision') && images && images.length > 0;
    
    // Prepare messages with image content if needed
    let processedMessages = [...messages];
    
    // If this is a vision request, add the image to the user message
    if (isVisionRequest) {
      // Find the user message to add the image to
      const userMessageIndex = processedMessages.findIndex(msg => msg.role === 'user');
      
      if (userMessageIndex !== -1) {
        // Convert the user message to content array format if it's not already
        const userMessage = processedMessages[userMessageIndex];
        let newContent = [];
        
        // Add the text content first
        if (typeof userMessage.content === 'string') {
          newContent.push({ type: 'text', text: userMessage.content });
        } else if (Array.isArray(userMessage.content)) {
          // If it's already an array, keep it as is
          newContent = [...userMessage.content];
        }
        
        // Add the images (limited by model.maxImages)
        const imagesToAdd = images.slice(0, model.maxImages || 1);
        imagesToAdd.forEach(image => {
          // Ensure we have a valid URL format
          let imageUrl = image;
          
          // Handle different image object formats
          if (typeof image === 'object') {
            imageUrl = image.url || image.src || image.source || image;
          }
          
          // Ensure imageUrl is a string
          if (typeof imageUrl !== 'string') {
            console.warn('Invalid image URL format:', image);
            return; // Skip this image
          }
          
          newContent.push({
            type: 'image_url',
            image_url: {
              url: imageUrl,
              detail: 'high'
            }
          });
        });
        
        // Replace the user message with the new content
        processedMessages[userMessageIndex] = {
          ...userMessage,
          content: newContent
        };
      }
    }

    // Prepare the request payload
    const payload = {
      model: model.internalName,
      messages: processedMessages,
      temperature,
      max_tokens
    };

    // Get the base URL for the API call
    const baseUrl = model.apiConfig?.baseUrl || '/api/openai';
    const isDirectApi = model.apiConfig?.apiKey && !baseUrl.includes('/api/');
    
    // Determine the endpoint based on whether this is a direct API call or through our proxy
    let url;
    
    // Use the standard chat completions endpoint for all requests
    if (isDirectApi) {
      // Direct API call to OpenAI
      url = 'https://api.openai.com/v1/chat/completions';
    } else {
      // Using our proxy API
      url = `${baseUrl}/v1/chat/completions`;
    }
    
    let headers;
    
    // Get API key from centralized storage if using direct API
    if (isDirectApi) {
      const apiKey = await getOpenAIApiKey();
      
      headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey || model.apiConfig?.apiKey}`
      };
    } else {
      // We've already set the URL for proxy endpoint above, no need to override it here
      // Get API key from centralized storage
      const providerApiKey = await getOpenAIApiKey();
      
      headers = {
        'Content-Type': 'application/json',
        'X-OpenAI-Key': providerApiKey || model.apiConfig?.apiKey
      };
    }
    
    // Debug logs to trace the URL configuration
    console.log('OpenAI API Configuration:', {
      baseUrl,
      isDirectApi,
      finalUrl: url,
      modelName: model.internalName,
      hasApiKey: !!(headers.Authorization || headers['X-OpenAI-Key'])
    });
    
    // Make the API call
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    // Get the response text content once to avoid 'body stream already read' error
    const responseText = await response.text();
    
    // Check if the response looks like HTML (starts with <!DOCTYPE or <html)
    if (responseText.trim().toLowerCase().startsWith('<!doctype') || 
        responseText.trim().toLowerCase().startsWith('<html')) {
      console.error('API returned HTML instead of JSON:', responseText.substring(0, 200));
      throw new Error(`API returned HTML instead of JSON. This usually indicates a server-side error or authentication issue.`);
    }
    
    // Check if response is OK
    if (!response.ok) {
      // Try to parse as JSON if it doesn't look like HTML
      let errorData;
      try {
        errorData = JSON.parse(responseText);
        throw new Error(errorData.error?.message || `OpenAI API error: ${response.status} ${response.statusText}`);
      } catch (parseError) {
        // If parsing fails, use the text content in the error
        throw new Error(`OpenAI API error: ${response.status} ${response.statusText}. Response: ${responseText.substring(0, 100)}...`);
      }
    }
    
    // If response is OK, try to parse the text as JSON
    try {
      const data = JSON.parse(responseText);
      return data;
    } catch (jsonError) {
      // Handle case where response is OK but not valid JSON
      throw new Error(`Failed to parse API response as JSON. Response: ${responseText.substring(0, 100)}...`);
    }
  } catch (error) {
    console.error('Error calling OpenAI:', error);
    throw error;
  }
};

/**
 * Make an API call to DeepSeek
 * @param {Object} model - The model configuration
 * @param {Object} requestData - The data to send to DeepSeek
 * @returns {Promise<Object>} - The response from DeepSeek
 */
const callDeepSeek = async (model, requestData) => {
  try {
    const { messages, temperature = 0.7, max_tokens = 2000 } = requestData;

    // Prepare the request payload
    const payload = {
      model: model.internalName,
      messages,
      temperature,
      max_tokens
    };

    // Use the custom base URL if provided, otherwise use the default
    const baseUrl = model.apiConfig?.baseUrl || 'https://api.deepseek.com/v1';
    
    // Get the API key from the centralized service
    const apiKey = await getProviderApiKey(model.provider);
    
    // Make the API call
    const response = await fetch(`${baseUrl}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey || model.apiConfig?.apiKey}`
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error?.message || `DeepSeek API error: ${response.status} ${response.statusText}`);
    }

    return data;
  } catch (error) {
    console.error('Error calling DeepSeek:', error);
    throw error;
  }
};

/**
 * Make an API call to Google Gemini
 * @param {Object} model - The model configuration
 * @param {Object} requestData - The data to send to Gemini
 * @returns {Promise<Object>} - The response from Gemini
 */
const callGemini = async (model, requestData) => {
  try {
    const { messages, temperature = 0.7, max_tokens = 2000, images = [], modelType } = requestData;

    // Check if this is a vision request with images
    const isVisionRequest = (modelType === 'vision' || model.type === 'vision') && images && images.length > 0;
    
    // Convert messages to Gemini format
    const geminiMessages = [];
    
    for (const message of messages) {
      if (message.role === 'system') {
        // Gemini doesn't have a system role, so we'll add it as a user message
        geminiMessages.push({
          role: 'user',
          parts: [{ text: `System: ${message.content}` }]
        });
      } else {
        const role = message.role === 'assistant' ? 'model' : message.role;
        const parts = [];
        
        // Handle text content
        if (typeof message.content === 'string') {
          parts.push({ text: message.content });
        } else if (Array.isArray(message.content)) {
          // Handle content array (for vision requests)
          for (const content of message.content) {
            if (content.type === 'text') {
              parts.push({ text: content.text });
            } else if (content.type === 'image_url') {
              // Convert image URL to Gemini format
              const imageUrl = content.image_url.url;
              if (imageUrl.startsWith('data:')) {
                // Handle base64 images
                const [mimeType, base64Data] = imageUrl.split(',');
                const mimeTypeMatch = mimeType.match(/data:([^;]+)/);
                parts.push({
                  inlineData: {
                    mimeType: mimeTypeMatch ? mimeTypeMatch[1] : 'image/jpeg',
                    data: base64Data
                  }
                });
              } else {
                // For regular URLs, we need to fetch and convert to base64
                try {
                  const response = await fetch(imageUrl);
                  const blob = await response.blob();
                  const base64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64String = reader.result.split(',')[1];
                      resolve(base64String);
                    };
                    reader.readAsDataURL(blob);
                  });
                  
                  parts.push({
                    inlineData: {
                      mimeType: blob.type || 'image/jpeg',
                      data: base64
                    }
                  });
                } catch (error) {
                  console.warn('Failed to fetch image for Gemini:', imageUrl, error);
                  // Skip this image if we can't fetch it
                }
              }
            }
          }
        } else {
          parts.push({ text: message.content });
        }
        
        geminiMessages.push({
          role,
          parts
        });
      }
    }
    
    // If this is a vision request and we have images not already in messages, add them
    if (isVisionRequest && images.length > 0) {
      // Check if images are already included in the messages (from OpenAI format)
      const hasImagesInMessages = geminiMessages.some(msg => 
        msg.parts && msg.parts.some(part => part.inlineData)
      );
      
      // Only add images separately if they're not already in the messages
      if (!hasImagesInMessages) {
        // Find the last user message to add images to
        const lastUserMessageIndex = geminiMessages.map(msg => msg.role).lastIndexOf('user');
        
        if (lastUserMessageIndex !== -1) {
          const imagesToAdd = images.slice(0, model.maxImages || 1);
          
          for (const image of imagesToAdd) {
            let imageUrl = image;
            
            // Handle different image object formats
            if (typeof image === 'object') {
              imageUrl = image.url || image.src || image.source || image;
            }
            
            if (typeof imageUrl === 'string') {
              try {
                if (imageUrl.startsWith('data:')) {
                  // Handle base64 images
                  const [mimeType, base64Data] = imageUrl.split(',');
                  const mimeTypeMatch = mimeType.match(/data:([^;]+)/);
                  geminiMessages[lastUserMessageIndex].parts.push({
                    inlineData: {
                      mimeType: mimeTypeMatch ? mimeTypeMatch[1] : 'image/jpeg',
                      data: base64Data
                    }
                  });
                } else {
                  // For regular URLs, fetch and convert to base64
                  const response = await fetch(imageUrl);
                  const blob = await response.blob();
                  const base64 = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64String = reader.result.split(',')[1];
                      resolve(base64String);
                    };
                    reader.readAsDataURL(blob);
                  });
                  
                  geminiMessages[lastUserMessageIndex].parts.push({
                    inlineData: {
                      mimeType: blob.type || 'image/jpeg',
                      data: base64
                    }
                  });
                }
              } catch (error) {
                console.warn('Failed to process image for Gemini:', imageUrl, error);
              }
            }
          }
        }
      }
    }

    // Prepare the request payload
    const payload = {
      contents: geminiMessages,
      generationConfig: {
        temperature,
        maxOutputTokens: max_tokens,
        topP: 0.9,
        topK: 40
      }
    };

    // Use the custom base URL if provided, otherwise use the default
    const baseUrl = model.apiConfig?.baseUrl || 'https://generativelanguage.googleapis.com/v1beta';
    
    // Get the API key from the centralized service or use the model's API key
    const apiKey = await getProviderApiKey(model.provider) || model.apiConfig?.apiKey;
    
    if (!apiKey) {
      throw new Error('Gemini API key is required. Please configure it in the provider settings.');
    }
    
    // Log what we're sending to Gemini
    console.log('Sending to Gemini API:', {
      modelName: model.internalName || model.modelId,
      messageCount: geminiMessages.length,
      imagesInLastMessage: geminiMessages[geminiMessages.length - 1]?.parts?.filter(part => part.inlineData).length || 0,
      totalParts: geminiMessages[geminiMessages.length - 1]?.parts?.length || 0
    });
    
    // Make the API call
    const response = await fetch(`${baseUrl}/models/${model.internalName || model.modelId}:generateContent?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error?.message || `Gemini API error: ${response.status} ${response.statusText}`);
    }

    // Check if we have a valid response
    if (!data.candidates || !data.candidates[0] || !data.candidates[0].content || !data.candidates[0].content.parts) {
      throw new Error('Invalid response from Gemini API');
    }

    // Get the response content
    let responseContent = data.candidates[0].content.parts[0].text;
    
    // Try to parse as JSON and handle nested structures
    try {
      const parsedResponse = JSON.parse(responseContent);
      
      // Handle nested structures that Gemini might return
      if (parsedResponse.product_summary && parsedResponse.product_summary.content) {
        // Extract content from nested structure and flatten it
        const flattenedResponse = {
          body_html: parsedResponse.product_summary.content,
          ...parsedResponse
        };
        
        // Remove the nested structure to avoid confusion
        delete flattenedResponse.product_summary;
        
        // Convert back to JSON string for consistent processing
        responseContent = JSON.stringify(flattenedResponse);
        
        console.log('Flattened Gemini nested response structure for template compatibility');
      }
    } catch (error) {
      // If it's not JSON, keep the original content
      console.log('Gemini response is not JSON, keeping as plain text');
    }

    // Convert Gemini response to OpenAI format for consistency
    return {
      choices: [
        {
          message: {
            role: 'assistant',
            content: responseContent
          },
          finish_reason: 'stop'
        }
      ],
      model: model.internalName || model.modelId,
      usage: data.usage || { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 }
    };
  } catch (error) {
    console.error('Error calling Gemini:', error);
    throw error;
  }
};

/**
 * Process a prompt with the appropriate AI provider
 * @param {Object} options - The processing options
 * @param {string} options.provider - The provider name (openai, deepseek, gemini)
 * @param {string} options.modelId - The model ID or name to use
 * @param {string} options.prompt - The prompt to process
 * @param {number} options.temperature - The temperature to use
 * @param {number} options.maxTokens - The maximum number of tokens to generate
 * @param {Object} options.userModelSettings - User-specific model settings
 * @param {string[]} options.images - The images to process (for vision models)
 * @param {string} options.templateType - The template type (product, image)
 * @param {string} options.systemMessage - The system message to use
 * @returns {Promise<string>} - The generated content
 */
export const processWithAIProvider = async (options) => {
  try {
    const { 
      provider, 
      modelId, 
      prompt, 
      systemMessage = 'You are a helpful assistant that generates content for a Shopify store. IMPORTANT: You must respond with valid JSON format only. Your entire response must be a properly formatted JSON object that can be parsed directly.',
      temperature = 0.7,
      maxTokens = 2000,
      userModelSettings,
      images = [],
      templateType = 'product',
      userId // Extract userId from options
    } = options;
    
    // Determine if this is an image analysis request
    const isImageRequest = templateType === 'image' && images && images.length > 0;
    
    // Use a specific system message for image analysis if needed
    let effectiveSystemMessage = systemMessage;
    
    // Customize system message based on provider for better compatibility
    if (provider.toLowerCase() === 'gemini') {
      if (isImageRequest) {
        const isSidebarOnly = options.sidebarOnly === true;
        
        if (isSidebarOnly) {
          effectiveSystemMessage = 'You are an AI assistant that generates product content for an e-commerce platform. The image provided shows a product — which could be clothing, electronics, accessories, or another item. Focus only on visible features like shape, color, material, and likely use. Keep the output concise, factual, and avoid guessing things that are not visible. IMPORTANT: Respond with plain text only, not JSON. Your response will be used directly as product content.';
        } else {
          effectiveSystemMessage = 'You are an AI assistant that generates product content for an e-commerce platform. The image provided shows a product. Focus only on visible features like shape, color, material, and likely use. Keep the output concise, factual, and avoid guessing things that are not visible. IMPORTANT: You must respond with valid JSON format only. Your entire response must be a properly formatted JSON object that can be parsed directly.';
        }
      } else {
        // For Gemini text templates, use a system message that ensures proper field naming
        effectiveSystemMessage = 'You are a helpful assistant that generates content for a Shopify store. IMPORTANT: You must respond with valid JSON format only. Your entire response must be a properly formatted JSON object that can be parsed directly. Use the exact field names requested in the prompt (e.g., if the prompt asks for a description, return it as "body_html" field, if it asks for title, return it as "title" field). Do not use nested structures or alternative field names.';
      }
    } else if (isImageRequest) {
      // Check if this is a sidebar-only template or field insertion template
      const isSidebarOnly = options.sidebarOnly === true;
      
      if (isSidebarOnly) {
        // For sidebar-only mode, use plain text response
        effectiveSystemMessage = 'You are an AI assistant that generates product content for an e-commerce platform. The image provided shows a product — which could be clothing, electronics, accessories, or another item. Focus only on visible features like shape, color, material, and likely use. Keep the output concise, factual, and avoid guessing things that are not visible. IMPORTANT: Respond with plain text only, not JSON. Your response will be used directly as product content.';
      } else {
        // For field insertion mode, use JSON response
        effectiveSystemMessage = 'You are an AI assistant that generates product content for an e-commerce platform. The image provided shows a product. Focus only on visible features like shape, color, material, and likely use. Keep the output concise, factual, and avoid guessing things that are not visible. IMPORTANT: You must respond with valid JSON format only. Your entire response must be a properly formatted JSON object that can be parsed directly.';
      }
    }
    
    // Prepare the request data with proper format for vision models
    let messages = [
      {
        role: 'system',
        content: effectiveSystemMessage
      }
    ];
    
    // For image requests, we need to format the user message differently
    if (isImageRequest && images && images.length > 0) {
      // Create a content array for the user message
      const userContent = [];
      
      // Add each image to the content array
      images.forEach(imageUrl => {
        if (typeof imageUrl === 'string') {
          userContent.push({
            type: 'image_url',
            image_url: {
              url: imageUrl,
              detail: 'high'
            }
          });
        }
      });
      
      // Add the text prompt after the images
      userContent.push({
        type: 'text',
        text: prompt
      });
      
      // Add the user message with the content array
      messages.push({
        role: 'user',
        content: userContent
      });
    } else {
      // For regular text requests, use the simple format
      messages.push({
        role: 'user',
        content: prompt
      });
    }
    
    // Prepare the request data
    const requestData = {
      messages,
      temperature: options.temperature || 0.7,
      max_tokens: options.maxTokens || 2000,
      images: options.images || [],
      modelType: options.modelType
    };
    
    let response;
    
    // Route to the appropriate provider
    switch (provider.toLowerCase()) {
      case 'openai':
        // Get the model configuration if we have a model ID
        if (modelId) {
          // Create a model configuration object
          const model = { 
            internalName: modelId, 
            provider: 'openai',
            type: options.modelType || 'text', // Ensure model type is set correctly
            apiConfig: {}
          };
          
          // Apply user settings if available
          if (userModelSettings?.openai) {
            console.log('Applying OpenAI user settings:', {
              hasApiKey: !!userModelSettings.openai.apiKey,
              hasBaseUrl: !!userModelSettings.openai.baseUrl,
              baseUrl: userModelSettings.openai.baseUrl
            });
            
            model.apiConfig.apiKey = userModelSettings.openai.apiKey;
            model.apiConfig.baseUrl = userModelSettings.openai.baseUrl;
          } else {
            console.log('No OpenAI user settings found');
          }
          
          // Pass userId to the OpenAI call if available
          response = await callOpenAI(model, requestData, userId);
        } else {
          throw new Error('Model ID is required for OpenAI');
        }
        break;
        
      case 'deepseek':
        // Get the model configuration if we have a model ID
        if (modelId) {
          // Create a model configuration object
          const model = { 
            internalName: modelId, 
            provider: 'deepseek',
            apiConfig: {}
          };
          
          // Apply user settings if available
          if (userModelSettings?.deepseek) {
            model.apiConfig.apiKey = userModelSettings.deepseek.apiKey;
            model.apiConfig.baseUrl = userModelSettings.deepseek.baseUrl;
          }
          
          response = await callDeepSeek(model, requestData);
        } else {
          throw new Error('Model ID is required for DeepSeek');
        }
        break;
        
      case 'gemini':
        // Get the model configuration if we have a model ID
        if (modelId) {
          // Create a model configuration object
          const model = { 
            internalName: modelId, 
            provider: 'gemini',
            apiConfig: {}
          };
          
          // Apply user settings if available
          if (userModelSettings?.gemini) {
            model.apiConfig.apiKey = userModelSettings.gemini.apiKey;
            model.apiConfig.baseUrl = userModelSettings.gemini.baseUrl;
          }
          
          response = await callGemini(model, requestData);
        } else {
          throw new Error('Model ID is required for Gemini');
        }
        break;
        
      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }
    
    // Extract the content from the response
    if (response.choices && response.choices.length > 0) {
      return response.choices[0].message.content;
    } else {
      throw new Error('No content generated by AI provider');
    }
  } catch (error) {
    console.error('Error processing with AI provider:', error);
    throw error;
  }
};

/**
 * Deduct credits from the user's account based on the model and template usage
 * @param {string} userId - The user ID
 * @param {string} modelId - The model ID
 * @param {number} outputCount - The number of output fields
 * @returns {Promise<Object>} - The result of the credit deduction
 */
export const deductCredits = async (userId, modelId, outputCount) => {
  try {
    // Validate userId is provided
    if (!userId) {
      console.error('No userId provided to deductCredits function');
      throw new Error('User authentication required for credit deduction');
    }
    
    // Get the model configuration
    const model = await getAIModel(modelId);
    if (!model) {
      throw new Error(`Model with ID ${modelId} not found`);
    }

    // Calculate the credit cost
    const creditCost = outputCount * (model.creditCostPerOutput || 1);

    // Import the credit service dynamically to avoid circular dependencies
    const { deductFlexCredits } = await import('./creditService');
    
    console.log(`Deducting ${creditCost} credits for user ${userId}`);
    
    // Deduct the credits
    const result = await deductFlexCredits(userId, creditCost);
    
    return {
      success: true,
      creditCost,
      remainingCredits: result.remainingCredits,
      model: model.displayName
    };
  } catch (error) {
    console.error('Error deducting credits:', error);
    throw error;
  }
};
